import type { ReactNode } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { useIsDesktop } from '@/app/useMediaQuery';
import { useHistoryLayer } from '@/app/useHistoryLayer';

export interface ResponsiveModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Right-hand side of the title row; only the sheet variant shows it. */
  titleAside?: ReactNode;
  srDescription?: string;
  children: ReactNode;
}

/**
 * One modal, two shapes: a bottom sheet on phones, a centred dialog from the
 * 900px breakpoint up. The content is identical, which is what the handoff asks
 * for — only the container changes.
 */
export function ResponsiveModal({
  open,
  onOpenChange,
  title,
  titleAside,
  srDescription,
  children,
}: ResponsiveModalProps) {
  const isDesktop = useIsDesktop();
  // Lets the Android back gesture dismiss the modal instead of the app.
  useHistoryLayer(open, () => onOpenChange(false));

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent title={title} srDescription={srDescription}>
          {children}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent title={title} titleAside={titleAside} srDescription={srDescription}>
        {children}
      </DrawerContent>
    </Drawer>
  );
}
